import React, { useState, useCallback, useEffect, useRef, useImperativeHandle, useMemo } from 'react';
import moment from 'moment';
import Filter, { IFilterInstance } from '@/pages/intelligence/components/Filter';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useGenreComponent from '@/pages/intelligence/hooks/useGenreComponent';
import useFilterUseHook from '@/pages/intelligence/hooks/useFilterUseHook';
import { COMPETITOR_GUIDE_CLASSNAMES } from '@intelligence/components/UserGuide/Competitor';
import {
  filteredObject,
  getFilterValueParams,
  timeFilterFormatFormValue,
  formatFilterSaveValue,
  FilterTimeType,
} from '@intelligence/utils/filterUtils';
import { getDeviceOptionByType } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import { useReleaseTime } from '@intelligence/hooks/useReleaseTime';
import {
  SENSOR_TOWER_TAG_KEYS,
  APPMAGIC_TAG_KEYS,
  APPMAGIC_TAG_THEMES,
  APPMAGIC_TAG_FEATURES,
  APPMAGIC_TAG_IP,
  APPMAGIC_TAG_SETTINGS,
  APPMAGIC_TAG_ART_STYLES,
  APPMAGIC_TAG_TITLE_MAP,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { saveFilterName } from '@/pages/intelligence/components/Filter/util';
import { getDbiUrlParams } from '@intelligence/components/Filter/util';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { useSelector, history, getLocale, useIntl } from 'umi';
import { feachTimeTypeToFilterTimeType, spreadIfObject } from '@/pages/intelligence/utils/common';
import { buildResetFilterValue } from './resetValue';
import { STREAMING_GAMETYPE_OPTIONS } from './constant';
import { applyFilterTipsToInputList } from '@intelligence/pages/TopCharts/_config/topChartsFilterTipsUtils';

import type { IGenretorValue } from '@intelligence/v3/utils/genre';

/**
 * CommonFilter组件实例
 */
export interface ICommonFilterInstance extends IFilterInstance {
  getFormValue: () => any;
  setFormValue: (payload: any) => void;
}

interface DbiCommonFilterProps {
  filterConfirm: Function;
  // 表单变更时的额外操作
  onChangeExtra?: Function;
  /**
   * 提供给外部调用者在setFormValue前根据自己业务调整formValue
   * @param 本次修改的筛选器的key
   * @param formValue
   * @return 调整后的formValue
   */
  adjustFormValue?: (key: string, formValue: any) => any;
  onChangeExtraLast?: Function;
  // 点击reset按钮提供给外面调用
  filterReset?: Function;
  timeFilterValues?: any;
  defaultValue: any;
  resetBaseValue?: any;
  filterValue: any;
  setFilterVale: any;
  filterCfg: any;
  // 给没有reset按钮提供reset
  isHandleReset?: boolean;
  // 切换日周月粒度时的时间默认值，拿的是后端返回有数据的时间用于切换
  rangeTimeComponentDefault?: any;
  rangePickerDisabledMap?: IDateRangeDisabledCallbackDateTypeMap | null;
  releaseTimeComponentDefault?: any;
  genreComponentDefault?: IGenretorValue | null;
  saveParamsMetricsSelection?: any;
  isNewFilterExtend?: boolean;
  // Release Time最小日期限制
  minReleaseTime?: string;
  // Release Time最大日期限制
  maxReleaseTime?: string;
  sinceReleaseTime?: string;
  earliestDataTime?: string;
  onToggleMoreFilter?: Function;
  resetTip?: React.ReactNode;
  buriedPointParams?: any;
  /** 榜单筛选继承作用域；不传时保持旧的全局 common 行为。 */
  extendPageId?: string;
}

function DbiCommonFilter(props: DbiCommonFilterProps, ref: React.Ref<ICommonFilterInstance>) {
  const {
    filterConfirm,
    onChangeExtra,
    adjustFormValue,
    onChangeExtraLast,
    filterReset,
    timeFilterValues,
    defaultValue = {},
    resetBaseValue,
    filterValue = {},
    setFilterVale,
    filterCfg,
    isHandleReset,
    rangeTimeComponentDefault,
    rangePickerDisabledMap,
    releaseTimeComponentDefault,
    genreComponentDefault,
    minReleaseTime: minReleaseTimeProps,
    maxReleaseTime: maxReleaseTimeProps,
    sinceReleaseTime,
    earliestDataTime,
    onToggleMoreFilter = () => { },
    saveParamsMetricsSelection,
    resetTip,
    isNewFilterExtend = false,
    // filterSaveType = '',
    buriedPointParams,
    extendPageId,
  } = props;
  const locale = getLocale();
  // const { formatMessage } = useIntl();

  // console.debug('DbiCommonFilter5555555555', props);

  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  // 继承逻辑在这写
  // formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
  const { setFilterExtend, resetFilterExtend } = useFilterExtend({
    pageId: extendPageId,
  });
  // const initialFormValue = filterValue;
  const [formValue, setFormValue] = useState(filterValue);
  const getCompaniesFetch = filterCfg?.getCompaniesFetch;
  const specialParams = filterCfg?.specialParams || {};
  const {
    regionOptions = [],
    getRadioOptionsTips,
    groupOptionsData = [],
    gsdDeviceOptions = [],
    gsdSegmentOptions = [],
    steamTagOptions = [],
    gameModeOptions = [],
    gameEngineOptions = [],
    publisherClassOptions = [],
    digitalDeviceOptions = getDeviceOptionByType('DigitalRevenue'),
    metricTableDeviceOptions = getDeviceOptionByType('PCConsoleMetricsTable'),
    physicalDeviceOptions = getDeviceOptionByType('PhysicalRevenue'),
    mauDeviceOptions = getDeviceOptionByType('MAU'),
    lifetimeDeviceOptions = getDeviceOptionByType('LifeTimePlayers'),
    allGames,
    sourceFilterSingleType = 'multiple',
    // ifNeedChangeValue = false,
  } = specialParams || {};

  const [resetFilterValue, setResetFilterValue] = useState(() =>
    buildResetFilterValue({
      filterKeyList: filterCfg?.filterKeyList,
      resetBaseValue,
      fallbackResetValue: filterCfg?.resetFilterValue,
      rangeTimeComponentDefault,
      releaseTimeComponentDefault,
      genreComponentDefault,
    }),
  );
  const formatFormValue = (values: any) => {
    const newFilterValues = formatFilterSaveValue({
      values,
      timeConfig: timeFilterFormatFormValue(specialParams.rangeTimeType),
      selectRegionConfig: {
        // 单选的国家筛选器
        isSingleCountry: specialParams.isSimpleCountry,
      },
    });
    return newFilterValues;
  };

  const {
    genreRef,
    genreSource,
    mainGenres,
    subGenres,
    handleGenresChange,
    handleReset: handleGenresReset,
  } = useGenreComponent(props);
  const companyRef = useRef();
  const developerRef = useRef();
  const steamTagRef = useRef();
  const gameModeRef = useRef();
  const gameEngineRef = useRef();
  const publisherClassRef = useRef();
  const { minReleaseTime, maxReleaseTime, disabledDate, getAllCompaniesFilterParams } =
    useReleaseTime({});

  // 筛选器配置
  const defaultInputList = {
    [filterKeyMap.ROW]: {
      savePopupVisble: 0,
      key: filterKeyMap.ROW,
    },
    [filterKeyMap.SUMMARY_CARD]: {
      savePopupVisble: 0,
      key: filterKeyMap.SUMMARY_CARD,
    },
    [filterKeyMap.SOURCE_EXPLANATION]: {
      savePopupVisble: 0,
      key: filterKeyMap.SOURCE_EXPLANATION,
    },
    [filterKeyMap.SAVE_FILTER_SETTINGS]: {
      savePopupVisble: 0,
      key: filterKeyMap.SAVE_FILTER_SETTINGS,
    },
    [filterKeyMap.RESET]: {
      savePopupVisble: 0,
      key: filterKeyMap.RESET,
    },
    [filterKeyMap.MY_FILTER]: {
      savePopupVisble: 0,
      key: filterKeyMap.MY_FILTER,
      value: defaultValue[filterKeyMap.MY_FILTER],
      style: specialParams?.myFilterStyle,
      className: specialParams?.myFilterClassName,
      hoverTip: specialParams?.filterTips?.[filterKeyMap.MY_FILTER],
      // filterSaveType,
    },
    [filterKeyMap.FOLLOWING_GLOUP]: {
      key: filterKeyMap.FOLLOWING_GLOUP,
      default: defaultValue[filterKeyMap.FOLLOWING_GLOUP],
      options: [],
      savePopupVisble: 0,
    },
    [filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO]: {
      savePopupVisble: 1,
      key: filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO,
    },
    [filterKeyMap.RANGE_TIME]: {
      savePopupVisble: 1,
      type: filterKeyMap.RANGE_TIME,
      key: filterKeyMap.RANGE_TIME,
      hoverTip: specialParams?.filterTips?.[filterKeyMap.RANGE_TIME],
      startOfWeek: specialParams?.startOfWeek ?? 0,
      startOfDate: specialParams?.startOfDate,
      autoRange:
        specialParams?.rangeTimeType === FilterTimeType.sameMonth
          ? {
            timeType: 'month',
            length: 0,
          }
          : undefined,
      typeList: specialParams?.typeList,
      showSelectType:
        specialParams?.showSelectType || specialParams?.showSelectType === false
          ? specialParams?.showSelectType
          : specialParams?.rangeTimeType === FilterTimeType.commonRange,

      showShortcut:
        specialParams?.showShortcut || specialParams?.rangeTimeType === FilterTimeType.commonRange,
      componentDefault: rangeTimeComponentDefault,
      rangeTimeOtherStyle: specialParams?.rangeTimeOtherStyle,
      earliestDataTime,
      sinceReleaseTime,
      isShowAlloftime: specialParams?.isShowAlloftime,
      ...spreadIfObject(specialParams?.[filterKeyMap.RANGE_TIME]),
    },
    [filterKeyMap.DAY_RANGE_TIME]: {
      // 新游的时间筛选器
      savePopupVisble: 1,
      type: filterKeyMap.RANGE_TIME,
      key: filterKeyMap.DAY_RANGE_TIME,
      calendarType: 'date',
      showSelectType: false,
      showShortcut: false,
      // disabledDate: disabledDate,
    },
    [filterKeyMap.REGION]: {
      savePopupVisble: 1,
      key: filterKeyMap.REGION,
      hoverTip: specialParams?.filterTips?.[filterKeyMap.REGION],
      default: defaultValue[filterKeyMap.REGION],
      visible: 1,
      disableNoChina: specialParams?.disableNoChina,
      options: [],
      globalInfo: specialParams?.globalInfo,
      globalInfoKey: specialParams?.globalInfoKey,
      globalWoChianInfoKey: specialParams?.globalWoChianInfoKey,
      entityType: specialParams?.entityType,
      isTopCompanies: specialParams?.isTopCompanies,
      className: specialParams?.regionClassName,
      showRadioBubble: specialParams?.showRadioBubble,
    },
    [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
      savePopupVisble: 1,
      key: filterKeyMap.COMPOSED_COUNTRY_SELECT,
      type: filterKeyMap.COMPOSED_COUNTRY_SELECT,
      // className: 'dbi-mobile-store-rank_composed-country-select',
      visible: 1,
      disabled: false,
      isShowTypeSelect: false,
      options: [],
    },
    [filterKeyMap.PLATFORM]: {
      savePopupVisble: 1,
      key: filterKeyMap.PLATFORM,
      hoverTip: specialParams?.filterTips?.[filterKeyMap.PLATFORM],
      default: defaultValue[filterKeyMap.PLATFORM],
      ...spreadIfObject(specialParams?.[filterKeyMap.PLATFORM]),
    },
    [filterKeyMap.KOL_PLATFORM]: {
      key: filterKeyMap.KOL_PLATFORM,
      default: defaultValue[filterKeyMap.KOL_PLATFORM],
    },
    [filterKeyMap.DATASOURCE]: {
      savePopupVisble: 1,
      key: filterKeyMap.DATASOURCE,
      default: defaultValue[filterKeyMap.DATASOURCE],
      showDatabrain: specialParams?.dataSourceShowDatabrain,
      singleType: sourceFilterSingleType,
      ...spreadIfObject(specialParams?.[filterKeyMap.DATASOURCE]),
    },
    [filterKeyMap.HOT_TAGS_SOURCE]: {
      savePopupVisble: 1,
      key: filterKeyMap.HOT_TAGS_SOURCE,
      options: specialParams?.hotTagsOptions || [],
      // 根据 URL filter_tag_name 找到默认选中项，使用 hotTagDefaultValue 避免被 inputDefaultVal 覆盖
      hotTagDefaultValue: specialParams?.filterTagName
        ? (specialParams?.hotTagsOptions || []).find((opt: any) => opt?.name === specialParams.filterTagName)
        : undefined,
      // HotTagsSource 选项变化时的回调，由外层 specialParams 注入
      onHotTagConfirm: specialParams?.onHotTagConfirm,
    },
    [filterKeyMap.PUBLISHER]: {
      key: filterKeyMap.PUBLISHER,
      title: 'dbi_metric_publisher',
      default: [],
      style: { width: '280px' },
      singleType: 'single',
      isShowButtonLabel: true,
      options: [
        {
          text: 'dbi_metric_publisher_All_value',
          value: 'all',
          // img: annieImg,
        },
        {
          text: 'dbi_metric_publisher_shared_value',
          value: 'NETWORK',
          // img: sensorImg,
          tip: 'dbi_metric_publisher_shared',
          tipMetrics: 'data_usage',
        },
        {
          text: 'dbi_metric_publisher_retail_value',
          value: 'RETAIL',
          // img: sensorImg,
          tip: 'dbi_metric_publisher_retail',
          tipMetrics: 'data_usage',
        },
      ],
    },
    [filterKeyMap.GENRETOR]: {
      savePopupVisble: 1,
      key: filterKeyMap.GENRETOR,
      hoverTip: specialParams?.filterTips?.[filterKeyMap.GENRETOR],
      // 这个className是 Competitor新手引导用到的
      className: specialParams?.genreClassName
        ? `${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT3} ${specialParams.genreClassName}`
        : COMPETITOR_GUIDE_CLASSNAMES.ELEMENT3,
      default: defaultValue[filterKeyMap.GENRETOR],
      isStdGenreValuesHandler: specialParams?.isStdGenreValuesHandler,
      overlayClassName: specialParams?.genreOverlayClassName,
      genresOptionsDataLoading: specialParams?.genreOptionsLoading,
      genresOptionsData: specialParams?.genreOptions,
      disableAnnie: specialParams?.disableAnnie,
      disableIegg: specialParams?.disableIegg,
      disableSensorTower: specialParams?.disableSensorTower,
      sources: specialParams?.genreSources,
      genreData: {
        genreRef,
        genreSource,
        mainGenres,
        subGenres,
        handleGenresChange,
        handleGenresReset,
      },
      entityType: specialParams?.entityType,
      buriedPointParams,
      overlayPlacement: specialParams?.genreOverlayPlacement,
    },
    // 带数据源切换Segment的品类筛选器
    [filterKeyMap.GENRETOR_WITH_SEGMENT]: {
      savePopupVisble: 1,
      key: filterKeyMap.GENRETOR_WITH_SEGMENT,
      hoverTip:
        specialParams?.filterTips?.[filterKeyMap.GENRETOR_WITH_SEGMENT] ??
        specialParams?.filterTips?.[filterKeyMap.GENRETOR],
      className: specialParams?.genreClassName
        ? `${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT3} ${specialParams.genreClassName}`
        : COMPETITOR_GUIDE_CLASSNAMES.ELEMENT3,
      default: defaultValue[filterKeyMap.GENRETOR] || defaultValue[filterKeyMap.GENRETOR_WITH_SEGMENT],
      isStdGenreValuesHandler: specialParams?.isStdGenreValuesHandler,
      overlayClassName: specialParams?.genreOverlayClassName,
      genresOptionsDataLoading: specialParams?.genreOptionsLoading,
      genresOptionsData: specialParams?.genreOptions,
      disableAnnie: specialParams?.disableAnnie,
      disableIegg: specialParams?.disableIegg,
      disableSensorTower: specialParams?.disableSensorTower,
      sources: specialParams?.genreSources,
      genreData: {
        genreRef,
        genreSource,
        mainGenres,
        subGenres,
        handleGenresChange,
        handleGenresReset,
      },
      entityType: specialParams?.entityType,
      buriedPointParams,
      overlayPlacement: specialParams?.genreOverlayPlacement,
    },
    [filterKeyMap.ALL_COMPANYS]: {
      savePopupVisble: 0,
      key: filterKeyMap.ALL_COMPANYS,
      title: specialParams?.allCompanyTitle || 'dbi_company',
      ref: companyRef,
      getFetchParams: () => filterCfg?.companyFilterParams || {},
      getCompanies: getCompaniesFetch,
      ...getAllCompaniesFilterParams(), // 分离出来的获取参数和成功后的处理函数
      // onFetchSuccess,
    },
    [filterKeyMap.PUBLISHER_COMPANY]: {
      savePopupVisble: 0,
      type: filterKeyMap.ALL_COMPANYS,
      key: filterKeyMap.PUBLISHER_COMPANY,
      title: 'dbi_publisher',
      ref: companyRef,
      getFetchParams: () => filterCfg?.companyFilterParams || {},
      getCompanies: getCompaniesFetch,
      ...getAllCompaniesFilterParams(),
    },
    [filterKeyMap.DEVELOPER]: {
      savePopupVisble: 0,
      type: filterKeyMap.ALL_COMPANYS,
      key: filterKeyMap.DEVELOPER,
      title: 'dbi_developer',
      ref: developerRef,
      getFetchParams: () => filterCfg?.companyFilterParams || {},
      getCompanies: getCompaniesFetch,
      ...getAllCompaniesFilterParams(),
    },
    [filterKeyMap.ALL_Games]: {
      savePopupVisble: 1,
      key: filterKeyMap.ALL_Games,
      default: defaultValue[filterKeyMap.ALL_Games],
      ...spreadIfObject(specialParams?.[filterKeyMap.ALL_Games]),
      // params: specialParams?.[filterKeyMap.ALL_Games]?.params,
      // getCompanies: getCompaniesFetch,
    },
    [filterKeyMap.RELEASE_TIME]: {
      savePopupVisble: 1,
      type: filterKeyMap.RANGE_TIME,
      key: filterKeyMap.RELEASE_TIME,
      hoverTip: specialParams?.filterTips?.[filterKeyMap.RELEASE_TIME],
      calendarType: 'date',
      showSelectType: false,
      showShortcut: false,
      disabledDate,
      componentDefault: releaseTimeComponentDefault,
    },
    [filterKeyMap.PUBLISHER_CLASS]: {
      savePopupVisble: 1,
      type: filterKeyMap.STEAM_TAGS,
      key: filterKeyMap.PUBLISHER_CLASS,
      title: 'dbi_publisher_class',
      AWidth: 440,
      ref: publisherClassRef,
      default: defaultValue[filterKeyMap.PUBLISHER_CLASS],
      selectorConfig: {
        placeholder: 'dbi_top_publisher_class_placeholder',
        searchPlaceholder: 'dbi_top_publisher_class_search_placeholder',
        label: 'dbi_publisher_class',
        tipLabel: 'dbi_publisher_class',
        tipText: 'dbi_top_publisher_class_tip',
        tipTextParams: {
          gamalyticDataSourceIcon: (
            <img style={{ verticalAlign: 'middle' }} src={getDataSourceIcon('gamalytic')} alt="" />
          ),
          alineaDataSourceIcon: (
            <img style={{ verticalAlign: 'middle' }} src={getDataSourceIcon('alinea')} alt="" />
          ),
          mscienceDataSourceIcon: (
            <img style={{ verticalAlign: 'middle' }} src={getDataSourceIcon('mscience')} alt="" />
          ),
        },
      },
      options: [],
    },
    [filterKeyMap.PUBLISHER_REGION]: {
      savePopupVisble: 1,
      type: filterKeyMap.REGION,
      key: filterKeyMap.PUBLISHER_REGION,
      title: 'dbi_publisher_region',
      default: defaultValue[filterKeyMap.PUBLISHER_REGION],
      hasInfoIcon: false,
      showRadioBubble: false,
    },
    [filterKeyMap.REVENUE_RANGE]: {
      title: 'dbi_revenue_range',
      clickSetDefault: true,
      savePopupVisble: 1,
      key: filterKeyMap.REVENUE_RANGE,
      default: defaultValue[filterKeyMap.REVENUE_RANGE],
    },
    [filterKeyMap.REVENUE_SCOPE]: {
      savePopupVisble: 1,
      key: filterKeyMap.REVENUE_SCOPE,
      default: defaultValue[filterKeyMap.REVENUE_SCOPE],
      ...spreadIfObject(specialParams?.[filterKeyMap.REVENUE_SCOPE]),
    },
    [filterKeyMap.REVENUE_GROWTH]: {
      savePopupVisble: 1,
      key: filterKeyMap.REVENUE_GROWTH,
      default: defaultValue[filterKeyMap.REVENUE_GROWTH],
    },
    [filterKeyMap.PCU_RANGE]: {
      unitText: '-',
      title: 'dbi_pcu_range',
      savePopupVisble: 1,
      leftPlaceholder: 'dbi_minimum',
      rightPlaceholder: 'dbi_maximum',
      key: filterKeyMap.PCU_RANGE,
      default: defaultValue[filterKeyMap.PCU_RANGE],
    },
    [filterKeyMap.WISHLISTS_RANGE]: {
      unitText: '-',
      title: 'dbi_wishlists_range',
      savePopupVisble: 1,
      leftPlaceholder: 'dbi_minimum',
      rightPlaceholder: 'dbi_maximum',
      type: filterKeyMap.PCU_RANGE,
      key: filterKeyMap.WISHLISTS_RANGE,
      default: defaultValue[filterKeyMap.WISHLISTS_RANGE],
    },
    [filterKeyMap.DIGITAL_RANGE]: {
      title: 'dbi_digital_revenue_range',
      width: 370,
      savePopupVisble: 1,
      key: filterKeyMap.DIGITAL_RANGE,
      default: defaultValue[filterKeyMap.DIGITAL_RANGE],
    },
    [filterKeyMap.PHYSICAL_RANGE]: {
      title: 'dbi_physical_revenue_range',
      width: 360,
      savePopupVisble: 1,
      key: filterKeyMap.PHYSICAL_RANGE,
      default: defaultValue[filterKeyMap.DIGITAL_RANGE],
    },
    [filterKeyMap.PRICE_RANGE]: {
      title: 'dbi_price_range',
      savePopupVisble: 1,
      leftPlaceholder: 'dbi_minimum',
      rightPlaceholder: 'dbi_maximum',
      key: filterKeyMap.PRICE_RANGE,
      default: defaultValue[filterKeyMap.PRICE_RANGE],
    },
    [filterKeyMap.PREMIUM_RANGE]: {
      title: 'dbi_premium_revenue_range',
      width: 360,
      savePopupVisble: 1,
      key: filterKeyMap.PREMIUM_RANGE,
      default: defaultValue[filterKeyMap.PREMIUM_RANGE],
    },
    [filterKeyMap.DIGITAL_SEGEMENT]: {
      savePopupVisble: 1,
      key: filterKeyMap.DIGITAL_SEGEMENT,
      default: defaultValue[filterKeyMap.DIGITAL_SEGEMENT],
      // 样式用于新手引导
      className: specialParams?.digitalSegementGuideClassName,
      AWidth: 356,
      tips: specialParams?.digitalSegementTips,
    },
    [filterKeyMap.DIGITAL_SEGMENT_MULTIPLE]: {
      savePopupVisble: 1,
      key: filterKeyMap.DIGITAL_SEGMENT_MULTIPLE,
      default: defaultValue[filterKeyMap.DIGITAL_SEGMENT_MULTIPLE],
      // 样式用于新手引导
      className: specialParams?.digitalSegementGuideClassName,
      AWidth: 356,
      tips: specialParams?.digitalSegementTips,
    },
    [filterKeyMap.ENGAGELEVEL_SEGEMENT]: {
      savePopupVisble: 1,
      key: filterKeyMap.ENGAGELEVEL_SEGEMENT,
      default: defaultValue[filterKeyMap.ENGAGELEVEL_SEGEMENT],
      // tips: specialParams?.digitalSegementTips,
    },
    [filterKeyMap.GSD_SEGEMENT]: {
      savePopupVisble: 1,
      key: filterKeyMap.GSD_SEGEMENT,
      default: defaultValue[filterKeyMap.GSD_SEGEMENT],
      options: gsdSegmentOptions,
    },
    [filterKeyMap.DIGITAL_DEVICE]: {
      savePopupVisble: 1,
      key: filterKeyMap.DIGITAL_DEVICE,
      type: filterKeyMap.CONSOLE_DEVICE,
      options: digitalDeviceOptions,
      default: defaultValue[filterKeyMap.DIGITAL_DEVICE],
      ...spreadIfObject(specialParams?.[filterKeyMap.DIGITAL_DEVICE]),
    },
    [filterKeyMap.METRICTABLE_DEVICE]: {
      savePopupVisble: 1,
      key: filterKeyMap.METRICTABLE_DEVICE,
      type: filterKeyMap.CONSOLE_DEVICE,
      options: metricTableDeviceOptions,
      showIcon: true,
      default: defaultValue[filterKeyMap.METRICTABLE_DEVICE],
      ...spreadIfObject(specialParams?.[filterKeyMap.METRICTABLE_DEVICE]),
    },
    [filterKeyMap.PLATFORMCASCADER]: {
      savePopupVisble: 1,
      key: filterKeyMap.PLATFORMCASCADER,
      type: 'cascader',
      visible: 1,
      visibleinlist: 1,
      options: specialParams?.platformCascaderOptions,
      default: defaultValue[filterKeyMap.PLATFORMCASCADER],
      ...spreadIfObject(specialParams?.[filterKeyMap.PLATFORMCASCADER]),
    },
    [filterKeyMap.PHYSICAL_DEVICE]: {
      savePopupVisble: 1,
      key: filterKeyMap.PHYSICAL_DEVICE,
      type: filterKeyMap.CONSOLE_DEVICE,
      options: physicalDeviceOptions,
      default: defaultValue[filterKeyMap.PHYSICAL_DEVICE],
      ...spreadIfObject(specialParams?.[filterKeyMap.PHYSICAL_DEVICE]),
    },
    [filterKeyMap.STEAM_TAGS]: {
      savePopupVisble: 1,
      key: filterKeyMap.STEAM_TAGS,
      AWidth: 440,
      ref: steamTagRef,
      default: defaultValue[filterKeyMap.STEAM_TAGS],
    },
    [filterKeyMap.GAME_MODE]: {
      savePopupVisble: 1,
      key: filterKeyMap.GAME_MODE,
      AWidth: 370,
      ref: gameModeRef,
      default: defaultValue[filterKeyMap.GAME_MODE],
      selectorConfig: {
        placeholder: 'dbi_top_game_mode_placeholder',
        searchPlaceholder: 'dbi_top_game_mode_search_placeholder',
        label: 'dbi_top_game_mode',
        tipLabel: 'dbi_top_game_mode',
        tipText: 'dbi_top_game_mode_tip',
      },
    },
    [filterKeyMap.GAME_ENGINE]: {
      savePopupVisble: 1,
      key: filterKeyMap.GAME_ENGINE,
      AWidth: 440,
      ref: gameEngineRef,
      default: defaultValue[filterKeyMap.GAME_ENGINE],
      selectorConfig: {
        placeholder: 'dbi_top_game_engine_placeholder',
        searchPlaceholder: 'dbi_top_game_engine_search_placeholder',
        label: 'dbi_top_game_engine',
        tipLabel: 'dbi_top_game_engine',
        tipText: 'dbi_top_game_engine_tip',
        logicLabel: 'dbi_game_engine_logic_label',
        logicTipText: 'dbi_game_engine_logic_tip',
      },
    },
    [filterKeyMap.MAU_DEVICE]: {
      savePopupVisble: 1,
      key: filterKeyMap.MAU_DEVICE,
      type: filterKeyMap.CONSOLE_DEVICE,
      options: mauDeviceOptions,
      default: defaultValue[filterKeyMap.MAU_DEVICE],
      ...spreadIfObject(specialParams?.[filterKeyMap.MAU_DEVICE]),
    },
    [filterKeyMap.LIFETIME_DEVICE]: {
      savePopupVisble: 1,
      key: filterKeyMap.LIFETIME_DEVICE,
      type: filterKeyMap.CONSOLE_DEVICE,
      options: lifetimeDeviceOptions,
      default: defaultValue[filterKeyMap.LIFETIME_DEVICE],
    },
    [filterKeyMap.GSD_DEVICE]: {
      savePopupVisble: 1,
      key: filterKeyMap.GSD_DEVICE,
      type: filterKeyMap.CONSOLE_DEVICE,
      options: gsdDeviceOptions || [],
      default: defaultValue[filterKeyMap.GSD_DEVICE],
      ...spreadIfObject(specialParams?.[filterKeyMap.GSD_DEVICE]),
    },
    [filterKeyMap.PHEAD_RANG]: {
      savePopupVisble: 1,
      key: filterKeyMap.PHEAD_RANG,
    },
    [filterKeyMap.PLAN]: {
      savePopupVisble: 1,
      key: filterKeyMap.PLAN,
      default: defaultValue[filterKeyMap.PLAN],
    },
    [filterKeyMap.SENSOR_TOWER_TAG_ART_STYLE]: {
      savePopupVisble: 1,
      key: 'sensor_tower_tag_art_style',
      title: 'dbi_detail_art_style',
      default: {
        value: [],
      },
    },
    [filterKeyMap.SENSOR_TOWER_TAG_CAMERA_POV]: {
      savePopupVisble: 1,
      key: 'sensor_tower_tag_camera_pov',
      title: 'dbi_detail_camera_pov',
      default: {
        value: [],
      },
    },
    [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
      savePopupVisble: 1,
      key: 'sensor_tower_tag_product_model',
      title: 'dbi_detail_product_model',
      default: {
        value: [],
      },
    },
    [filterKeyMap.SENSOR_TOWER_TAG_SETTING]: {
      savePopupVisble: 1,
      key: 'sensor_tower_tag_setting',
      title: 'dbi_detail_setting',
      default: {
        value: [],
      },
    },
    [filterKeyMap.SENSOR_TOWER_TAG_TAGS]: {
      savePopupVisble: 1,
      key: 'sensor_tower_tag_tags',
      title: 'dbi_detail_tags',
      default: {
        value: [],
      },
    },
    [filterKeyMap.SENSOR_TOWER_TAG_THEME]: {
      savePopupVisble: 1,
      key: 'sensor_tower_tag_theme',
      title: 'dbi_detail_theme',
      default: {
        value: [],
      },
    },
    [filterKeyMap.SENSOR_TOWER_TAG_MONETIZATION]: {
      savePopupVisble: 1,
      key: 'sensor_tower_tag_monetization',
      title: 'dbi_detail_monetization',
      default: {
        value: [],
      },
    },
    [filterKeyMap.APPMAGIC_TAG_THEMES]: {
      savePopupVisble: 1,
      key: APPMAGIC_TAG_THEMES,
      title: APPMAGIC_TAG_TITLE_MAP[APPMAGIC_TAG_THEMES],
      default: {
        value: [],
      },
    },
    [filterKeyMap.APPMAGIC_TAG_FEATURES]: {
      savePopupVisble: 1,
      key: APPMAGIC_TAG_FEATURES,
      title: APPMAGIC_TAG_TITLE_MAP[APPMAGIC_TAG_FEATURES],
      default: {
        value: [],
      },
    },
    [filterKeyMap.APPMAGIC_TAG_IP]: {
      savePopupVisble: 1,
      key: APPMAGIC_TAG_IP,
      title: APPMAGIC_TAG_TITLE_MAP[APPMAGIC_TAG_IP],
      default: {
        value: [],
      },
    },
    [filterKeyMap.APPMAGIC_TAG_SETTINGS]: {
      savePopupVisble: 1,
      key: APPMAGIC_TAG_SETTINGS,
      title: APPMAGIC_TAG_TITLE_MAP[APPMAGIC_TAG_SETTINGS],
      default: {
        value: [],
      },
    },
    [filterKeyMap.APPMAGIC_TAG_ART_STYLES]: {
      savePopupVisble: 1,
      key: APPMAGIC_TAG_ART_STYLES,
      title: APPMAGIC_TAG_TITLE_MAP[APPMAGIC_TAG_ART_STYLES],
      default: {
        value: [],
      },
    },
    [filterKeyMap.COMPETITOR_PLATFORM]: {
      savePopupVisble: 1,
      key: filterKeyMap.COMPETITOR_PLATFORM,
      buttonTitle: 'dbi_platform2',
      options: [],
    },
    [filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]: {
      savePopupVisble: 1,
      key: filterKeyMap.METRIC_SELECT_FOR_KPI_TREND,
      // buttonTitle: 'dbi_platform',
      // options: COMPETITOR_PLATFORM_OPTIONS,
      saveParamsMetricsSelection,
    },
    [filterKeyMap.STREAMING_GAME_TYPE]: {
      savePopupVisble: 1,
      key: filterKeyMap.STREAMING_GAME_TYPE,
      buttonTitle: 'dbi_selector_game_type',
      options: STREAMING_GAMETYPE_OPTIONS,
      style: { width: 258 },
    },
    [filterKeyMap.GAME_TYPE]: {
      savePopupVisble: 1,
      key: filterKeyMap.GAME_TYPE,
    },
    [filterKeyMap.IPO_STATUS]: {
      savePopupVisble: 1,
      key: filterKeyMap.IPO_STATUS,
    },
    [filterKeyMap.COUNTRY]: {
      savePopupVisble: 1,
      key: filterKeyMap.COUNTRY,
      title: 'dbi_company_headquaters',
      options: [],
    },
    [filterKeyMap.REVENUE_RANGE_UNIT]: {
      savePopupVisble: 1,
      title: 'dbi_revenue_range_unit',
      type: filterKeyMap.REVENUE_RANGE_UNIT,
      key: filterKeyMap.REVENUE_RANGE_UNIT,
    },
    [filterKeyMap.ACTIVE_USERS_RANGE]: {
      savePopupVisble: 1,
      title: 'dbi_active_users_range',
      type: filterKeyMap.ACTIVE_USERS_RANGE,
      key: filterKeyMap.ACTIVE_USERS_RANGE,
      leftPlaceholder: 'dbi_minimum',
      rightPlaceholder: 'dbi_maximum',
      default: defaultValue[filterKeyMap.ACTIVE_USERS_RANGE],
    },
    [filterKeyMap.ARPU_RANGE]: {
      savePopupVisble: 1,
      title: 'dbi_arpu_range',
      type: filterKeyMap.ARPU_RANGE,
      key: filterKeyMap.ARPU_RANGE,
      leftPlaceholder: 'dbi_minimum',
      rightPlaceholder: 'dbi_maximum',
      default: defaultValue[filterKeyMap.ARPU_RANGE],
    },
    [filterKeyMap.DOWNLOADS_RANGE]: {
      savePopupVisble: 1,
      title: 'dbi_downloads_range',
      type: filterKeyMap.DOWNLOADS_RANGE,
      key: filterKeyMap.DOWNLOADS_RANGE,
      leftPlaceholder: 'dbi_minimum',
      rightPlaceholder: 'dbi_maximum',
      default: defaultValue[filterKeyMap.DOWNLOADS_RANGE],
    },
    [filterKeyMap.RPD_RANGE]: {
      savePopupVisble: 1,
      title: 'dbi_rpd_range',
      titleDefaultMessage: locale === 'zh-CN' ? 'RPD区间' : 'RPD Range',
      type: filterKeyMap.RPD_RANGE,
      key: filterKeyMap.RPD_RANGE,
      leftPlaceholder: 'dbi_minimum',
      rightPlaceholder: 'dbi_maximum',
      default: defaultValue[filterKeyMap.RPD_RANGE],
    },
    ...filterKeyMap.buildNumberRangeInputListItems(
      filterKeyMap.RETENTION_D_RANGE_DEFS,
      defaultValue,
      locale,
    ),
    [filterKeyMap.DATASOURCESIM]: {
      key: filterKeyMap.DATASOURCESIM,
      default: defaultValue[filterKeyMap.DATASOURCESIM],
      ...spreadIfObject(specialParams?.[filterKeyMap.DATASOURCESIM]),
      isShowButtonLabel: true,
    },
    [filterKeyMap.RETENTION]: {
      key: filterKeyMap.RETENTION,
      default: defaultValue[filterKeyMap.RETENTION],
    },
    [filterKeyMap.APP_SELECTOR]: {
      key: filterKeyMap.APP_SELECTOR,
      default: defaultValue[filterKeyMap.APP_SELECTOR],
      options: [],
    },
    [filterKeyMap.STREAMING_PLATFORM]: {
      type: filterKeyMap.STREAMING_PLATFORM,
      key: filterKeyMap.STREAMING_PLATFORM,
      default: defaultValue[filterKeyMap.STREAMING_PLATFORM],
    },
    [filterKeyMap.MOBILE_OVERLAP]: {
      savePopupVisble: 1,
      key: filterKeyMap.MOBILE_OVERLAP,
      title: 'dbi_metric_mobile_cross_usage',
      default: {
        value: [
          { opt: '', value: '' },
        ],
      },
    },
  } as any;
  const list =
    filterCfg?.filterKeyList?.map(
      (t: string) => filterCfg?.inputListExtras?.[t] || defaultInputList?.[t] || {},
    ) || [];
  const [inputList, setInputList] = useState(list);

  // 切换数据源等场景下，specialParams 会更新；这里需要同步刷新 RANGE_TIME 的渲染配置（否则仍沿用旧的 typeList/showSelectType）
  useEffect(() => {
    if (!filterCfg?.filterKeyList?.includes(filterKeyMap.RANGE_TIME)) return;

    setInputList((prev: any[]) =>
      prev?.map((item: any) =>
        item?.key === filterKeyMap.RANGE_TIME
          ? defaultInputList?.[filterKeyMap.RANGE_TIME]
          : item,
      ),
    );

  }, [
    JSON.stringify(filterCfg?.filterKeyList || []),
    specialParams?.rangeTimeType,
    specialParams?.showSelectType,
    specialParams?.showShortcut,
    JSON.stringify(specialParams?.typeList || []),
    JSON.stringify(specialParams?.[filterKeyMap.RANGE_TIME] || {}),
  ]);

  // 更新筛选器inputList配置，如国家列表等需要后端返回的配置
  useEffect(() => {
    setInputList((prev: any[]) => {
      const updateInputList = prev?.map((item: any) => {


        if (saveParamsMetricsSelection) {
          if (item?.key === filterKeyMap.METRIC_SELECT_FOR_KPI_TREND) {
            return { ...item, saveParamsMetricsSelection };
          }
        }
        // 更新国家筛选器列表
        if (regionOptions || getRadioOptionsTips) {
          if (item?.key === filterKeyMap.REGION) {
            return {
              ...item,
              getRadioOptionsTips,
              options: regionOptions,
              hoverTip: defaultInputList?.[filterKeyMap.REGION]?.hoverTip,
              // value: ifNeedChangeValue
              //   ? specialParams?.regionOptionsNewValue || item.value
              //   : item.value,
              // disableNoChina: ifNeedChangeValue
              //   ? specialParams?.disableNoChina || item.disableNoChina
              //   : item.disableNoChina,
              // disableRegionGlobal: ifNeedChangeValue
              //   ? specialParams?.disableRegionGlobal || item.disableRegionGlobal
              //   : item.disableRegionGlobal,
            };
          }
          if (
            item?.key === filterKeyMap.COMPOSED_COUNTRY_SELECT ||
            item?.key === filterKeyMap.PUBLISHER_REGION
          ) {
            return { ...item, options: regionOptions };
          }
        }
        // 更新 SteamTag 筛选器列表
        if (steamTagOptions?.length && item?.key === filterKeyMap.STEAM_TAGS) {
          return {
            ...item,
            options: steamTagOptions,
          };
        }
        // 更新 GameMode 筛选器列表
        if (gameModeOptions?.length && item?.key === filterKeyMap.GAME_MODE) {
          return {
            ...item,
            options: gameModeOptions,
          };
        }
        // 更新 GameEngine 筛选器列表
        if (gameEngineOptions?.length && item?.key === filterKeyMap.GAME_ENGINE) {
          return {
            ...item,
            options: gameEngineOptions,
          };
        }
        if (publisherClassOptions?.length && item?.key === filterKeyMap.PUBLISHER_CLASS) {
          return {
            ...item,
            options: publisherClassOptions,
          };
        }
        // 更新 HotTagsSource 筛选器列表（异步请求完成后更新）
        if (specialParams?.hotTagsOptions?.length && item?.key === filterKeyMap.HOT_TAGS_SOURCE) {
          return {
            ...item,
            options: specialParams.hotTagsOptions,
            // 同步更新 hotTagDefaultValue（options 更新后重新匹配）
            hotTagDefaultValue: specialParams?.filterTagName
              ? specialParams.hotTagsOptions.find((opt: any) => opt?.name === specialParams.filterTagName)
              : item.hotTagDefaultValue,
          };
        }
        // 更新 allGames 快捷选项
        if (allGames?.selectedShortcutActive != null && item?.key === filterKeyMap.ALL_Games) {
          return {
            ...item,
            selectedShortcutActive: allGames?.selectedShortcutActive,
          };
        }

        if (item?.key === filterKeyMap.DIGITAL_SEGEMENT && specialParams?.digitalSegementTips) {
          return { ...item, tips: specialParams?.digitalSegementTips };
        }
        if (
          item?.key === filterKeyMap.COUNTRY &&
          specialParams?.[filterKeyMap.COUNTRY]?.options?.length
        ) {
          return {
            ...item,
            value: specialParams?.[filterKeyMap.COUNTRY]?.value,
            options: specialParams?.[filterKeyMap.COUNTRY]?.options,
          };
        }
        if (digitalDeviceOptions) {
          if (item?.key === filterKeyMap.DIGITAL_DEVICE) {
            return { ...item, options: digitalDeviceOptions };
          }
        }
        if (metricTableDeviceOptions) {
          if (item?.key === filterKeyMap.METRICTABLE_DEVICE) {
            return {
              ...item,
              options: metricTableDeviceOptions,
              // value: ifNeedChangeValue
              //   ? specialParams?.metricTableDeviceOptionsNewValue || item.value
              //   : item.value,
            };
          }
        }
        if (physicalDeviceOptions) {
          if (item?.key === filterKeyMap.PHYSICAL_DEVICE) {
            return { ...item, options: physicalDeviceOptions };
          }
        }
        if (mauDeviceOptions) {
          if (item?.key === filterKeyMap.MAU_DEVICE) {
            return { ...item, options: mauDeviceOptions };
          }
        }
        if (lifetimeDeviceOptions) {
          if (item?.key === filterKeyMap.LIFETIME_DEVICE) {
            return { ...item, options: lifetimeDeviceOptions };
          }
        }
        if (gsdDeviceOptions) {
          if (item?.key === filterKeyMap.GSD_DEVICE) {
            return { ...item, options: gsdDeviceOptions };
          }
        }
        if (gsdSegmentOptions) {
          if (item?.key === filterKeyMap.GSD_SEGEMENT) {
            return { ...item, options: gsdSegmentOptions };
          }
        }
        const { options: appidsOptions, value: appidsValue } =
          specialParams?.[filterKeyMap.APP_SELECTOR] || {};
        if (appidsOptions || appidsValue) {
          if (item?.key === filterKeyMap.APP_SELECTOR) {
            return {
              ...item,
              options: appidsOptions || [],
              value: appidsValue || '',
            };
          }
        }
        // 更新Release时间范围
        if (item?.key === filterKeyMap.RELEASE_TIME) {
          const minReleaseTimeTmp = minReleaseTimeProps || minReleaseTime;
          const maxReleaseTimeTmp = maxReleaseTimeProps || maxReleaseTime;
          const minReleaseTimeM = !minReleaseTimeTmp ? undefined : moment(minReleaseTimeTmp);
          const maxReleaseTimeM = !maxReleaseTimeTmp ? undefined : moment(maxReleaseTimeTmp);
          return {
            ...item,
            range: [minReleaseTimeM, maxReleaseTimeM],
          };
        }
        if (groupOptionsData) {
          if (item?.key === filterKeyMap.FOLLOWING_GLOUP) {
            return { ...item, options: groupOptionsData };
          }
        }

        if (item?.key === filterKeyMap.RANGE_TIME) {
          // 这里必须合并最新的 RANGE_TIME 配置（showSelectType/typeList 等），否则切源后仍可能沿用旧样式
          let newItem = { ...item, ...defaultInputList?.[filterKeyMap.RANGE_TIME] };


          // 更新最早有数据时间
          if (rangeTimeComponentDefault || earliestDataTime || sinceReleaseTime) {
            newItem = {
              ...newItem,
              componentDefault: rangeTimeComponentDefault,
              earliestDataTime,
              sinceReleaseTime,
            };
          }

          if (!!rangePickerDisabledMap) {
            newItem = {
              ...newItem,
              disabledDate: rangePickerDisabledMap?.daily,
              disabledWeek: rangePickerDisabledMap?.weekly,
              disabledMonth: rangePickerDisabledMap?.monthly,
            };
          }

          return newItem;
        }

        if (item?.key === filterKeyMap.GENRETOR) {
          return {
            ...item,
            hoverTip: specialParams?.filterTips?.[filterKeyMap.GENRETOR],
            genresOptionsDataLoading: filterCfg?.specialParams?.genreOptionsLoading,
            genresOptionsData: filterCfg?.specialParams?.genreOptions,
            overlayPlacement: filterCfg?.specialParams?.genreOverlayPlacement,
          };
        }

        // 带数据源切换Segment的品类筛选器
        if (item?.key === filterKeyMap.GENRETOR_WITH_SEGMENT) {
          return {
            ...item,
            hoverTip: specialParams?.filterTips?.[filterKeyMap.GENRETOR_WITH_SEGMENT],
            genresOptionsDataLoading: filterCfg?.specialParams?.genreOptionsLoading,
            genresOptionsData: filterCfg?.specialParams?.genreOptions,
            overlayPlacement: filterCfg?.specialParams?.genreOverlayPlacement,
          };
        }

        if ([...SENSOR_TOWER_TAG_KEYS]?.includes(item?.key)) {
          return {
            ...item,
            options: filterCfg?.specialParams?.sensorTowerTagConfig?.[item?.key],
          };
        }

        // 更新 App Magic Tag 筛选器列表
        if ([...APPMAGIC_TAG_KEYS]?.includes(item?.key)) {
          const appMagicConfig = filterCfg?.specialParams?.[item?.key];
          if (appMagicConfig) {
            return {
              ...item,
              options: appMagicConfig?.options || [],
              title: appMagicConfig?.title || item?.title,
              icon: appMagicConfig?.icon,
            };
          }
        }

        // 更新 REGION / PLATFORM 的 hoverTip
        if (item?.key === filterKeyMap.REGION || item?.key === filterKeyMap.PLATFORM) {
          const newHoverTip = defaultInputList?.[item.key]?.hoverTip;
          if (newHoverTip !== item?.hoverTip) {
            return {
              ...item,
              hoverTip: newHoverTip,
            };
          }
        }

        return item;
      });

      return updateInputList;
    });
  }, [

    JSON.stringify(regionOptions),
    JSON.stringify(getRadioOptionsTips),
    JSON.stringify(steamTagOptions),
    JSON.stringify(gameModeOptions),
    JSON.stringify(gameEngineOptions),
    minReleaseTimeProps,
    maxReleaseTimeProps,
    minReleaseTime,
    maxReleaseTime,
    JSON.stringify(groupOptionsData),
    // JSON.stringify(filterCfg?.specialParams?.sensorTowerTagConfig),
    // 细粒度依赖，避免 hotTagsOptions 等异步更新导致不必要的 inputList 刷新触发二次请求
    filterCfg?.specialParams?.dataSourceShowDatabrain,
    JSON.stringify(filterCfg?.specialParams?.sensorTowerTagConfig),
    JSON.stringify(filterCfg?.specialParams?.appidsOptions),
    JSON.stringify({
      options: (filterCfg?.specialParams?.[filterKeyMap.APP_SELECTOR]?.options || []).map(
        (opt: any) => opt?.value,
      ),
      value: filterCfg?.specialParams?.[filterKeyMap.APP_SELECTOR]?.value,
    }),
    filterCfg?.specialParams?.digitalSegementTips,
    JSON.stringify(filterCfg?.specialParams?.[filterKeyMap.COUNTRY]),
    JSON.stringify(filterCfg?.specialParams?.hotTagsOptions),
    filterCfg?.specialParams?.filterTagName,
    filterCfg?.specialParams?.onHotTagConfirm,
    rangeTimeComponentDefault,
    earliestDataTime,
    sinceReleaseTime,
    JSON.stringify(gsdDeviceOptions),
    JSON.stringify(gsdSegmentOptions),
    JSON.stringify(digitalDeviceOptions),
    JSON.stringify(metricTableDeviceOptions),
    JSON.stringify(physicalDeviceOptions),
    JSON.stringify(mauDeviceOptions),
    JSON.stringify(lifetimeDeviceOptions),
    JSON.stringify(publisherClassOptions),
    locale,
    JSON.stringify(filterCfg?.specialParams?.filterTips),
    JSON.stringify(saveParamsMetricsSelection),
    // filterSaveType,
    rangePickerDisabledMap,
  ]);

  /** 时间筛选器值由父组件更新 */
  const updateFormValue = (params?: any) => {
    try {
      const formUpdateValue = formValue;
      let newFormValue = {
        ...formUpdateValue,
      };
      const urlParams: any = getDbiUrlParams(history);
      // const hasUrlShortLink = !!history?.location?.query?.[URL_SHORT_LINK_NAME];
      if (Object.keys(urlParams)?.length) {
        // 优先使用url的值
        saveFilterName(userInfo, ''); // 清除缓存
        // 如果展示保存筛选器才
      } else if (filterCfg?.haveFilterSaveBtn && filterSaveName && filterSaveData) {
        // 否则使用保存的值，最后才是继承的值
        const filterValues = filterSaveData?.value;
        const formatFilterValues = formatFormValue(filterValues);
        const filterSaveValue = {
          ...defaultValue,
          ...formatFilterValues,
          [filterKeyMap.MY_FILTER]: {
            value: filterSaveName,
          },
        };
        newFormValue = { ...newFormValue, ...filterSaveValue };
      }
      // 一般传过来的是要更新后端返回的时间筛选器的值，在使用短链接在接口返回后在页面更新则这里可弃用
      if (params && Object.prototype.toString.call(params) === '[object Object]') {
        newFormValue = { ...newFormValue, ...params };
      }
      const start = newFormValue?.[filterKeyMap.RANGE_TIME]?.value?.[0];
      const end = newFormValue?.[filterKeyMap.RANGE_TIME]?.value?.[1];

      const isValidDate = (date: string) => date && moment(date).isValid();
      // 更新接口返回的时间
      if (
        !isValidDate(start) &&
        !isValidDate(end) &&
        Array.isArray(timeFilterValues?.value) &&
        timeFilterValues?.value.length === 2
      ) {
        newFormValue = {
          ...newFormValue,
          [filterKeyMap.RANGE_TIME]: {
            type: feachTimeTypeToFilterTimeType(timeFilterValues?.type), // TODO
            value: [moment(timeFilterValues?.value[0]), moment(timeFilterValues?.value[1])],
          },
        };
      }
      setFormValue(newFormValue);
    } catch (err) {
      console.error(err);
    }
  };

  /** 时间筛选器值如时间筛选器、保存筛选器的值、 */
  useEffect(() => {
    updateFormValue(filterValue);
  }, [
    filterSaveName,
    JSON.stringify(filterValue),
    JSON.stringify(filterSaveData),
    JSON.stringify(timeFilterValues),
  ]);

  const onConfirm = useCallback(
    (
      /** 筛选器数据 */
      value,
      /** 筛选器转换后的数据 */
      apiValue,
      isCustomLayoutFilter = false,
    ) => {
      let valueTemp = value;
      let apiValueTemp = apiValue;
      // console.log('onConfirm-isCustomLayoutFilterisCustomLayoutFilter', isCustomLayoutFilter);
      if (isCustomLayoutFilter) {
        valueTemp = {
          ...valueTemp,
          myFilter: {
            value: '',
          },
        };
        apiValueTemp = {
          ...apiValueTemp,
          myFilter: {
            value: '',
          },
        };
      }
      setFormValue(valueTemp);
      const newFilterKeyList: string[] = filterCfg?.filterKeyList;
      // getFilterKeyListHasCountry(filterCfg?.filterKeyList);
      const validFilter = filteredObject(apiValueTemp, newFilterKeyList);
      const { params, updateFilterData } = getFilterValueParams(
        validFilter, // apiValue,
        specialParams,
        newFilterKeyList,
      );
      // console.log('onConfirm-params', params);
      // 点击确认后，将筛选器保存方案及保存数据存到缓存中
      onSaveFilterMsg(valueTemp, apiValueTemp);
      setFilterVale?.(updateFilterData);
      // 组装成页面需要的参数
      const apiParams = filterCfg?.handleFilterToApiParams(params, updateFilterData);
      // console.log('onConfirm-apiParams', apiParams);
      filterConfirm?.(apiParams, apiValueTemp, apiValueTemp?.[filterKeyMap.MY_FILTER]?.value);
      // 榜单国家筛选器在confirm过滤继承值后需要更新继承值，
      // feat国家筛选闪动问题,setFilterExtend放在后面,

      if (!isNewFilterExtend) {
        // console.log('apiValueTempapiValueTemp',apiValueTemp)
        setFilterExtend(apiValueTemp);
      }
    },
    [filterConfirm],
  );

  const onReset = () => {
    const newFormValue = resetFilterValue; // filterCfg?.resetFilterValue;
    setFormValue(newFormValue);
    genreRef.current?.reset();
    companyRef?.current?.reset();
    developerRef?.current?.reset();
    steamTagRef?.current?.reset();
    gameModeRef?.current?.reset();
    gameEngineRef?.current?.reset();
    publisherClassRef?.current?.reset();
    resetFilterExtend();
    // setFilterVale?.(newFormValue);
    if (filterReset instanceof Function) {
      filterReset(newFormValue);
    }

    // 如果有默认 HotTag，reset 完成后重新触发 HotTag 选中
    // 走统一的下拉选项逻辑，恢复默认 HotTag 及其对应的筛选条件
    const { onHotTagConfirm: resetHotTagConfirm, hotTagDefaultOption: resetHotTagDefaultOption } = specialParams;
    if (resetHotTagDefaultOption && resetHotTagConfirm) {
      resetHotTagConfirm(resetHotTagDefaultOption);
    }
  };

  // 是否支持外部点击其他地方要调用reset恢复默认，如Following页
  useEffect(() => {
    if (isHandleReset) {
      onReset();
    }
  }, [JSON.stringify(isHandleReset)]);

  useEffect(() => {
    setResetFilterValue(
      buildResetFilterValue({
        filterKeyList: filterCfg?.filterKeyList,
        resetBaseValue,
        fallbackResetValue: filterCfg?.resetFilterValue,
        rangeTimeComponentDefault,
        releaseTimeComponentDefault,
        genreComponentDefault,
      }),
    );
  }, [
    JSON.stringify(resetBaseValue),
    JSON.stringify(filterCfg?.resetFilterValue),
    JSON.stringify(filterCfg?.filterKeyList || []),
    JSON.stringify(rangeTimeComponentDefault),
    JSON.stringify(releaseTimeComponentDefault),
    JSON.stringify(genreComponentDefault),
  ]);

  const {
    filterSaveName,
    filterSaveData,
    // updateFormValue,
    onSaveFilterMsg,
    onFilterChange,
  } = useFilterUseHook({
    defaultValue,
    formValue,
    setFormValue,
    timeFilterValues,
    formatFormValue,
    onConfirm,
    onChangeExtra,
    adjustFormValue,
    onChangeExtraLast,
    inputList,
    filterSaveType: filterCfg?.filterSaveType,
  });

  const filterRef = useRef<IFilterInstance | null>(null);

  const inputListWithTips = useMemo(
    () => applyFilterTipsToInputList(inputList, specialParams?.filterTips),
    [inputList, specialParams?.filterTips],
  );

  useImperativeHandle(ref, () => ({
    getFormValue: () => {
      return { ...formValue };
    },
    setFormValue: (payload: any) => {
      setFormValue({ ...formValue, ...payload });
    },
    setValueAndConfirm: (partialValue: IRecord, options?: { resetToDefault?: boolean }) => {
      filterRef.current?.setValueAndConfirm(partialValue, options);
    },
  }));

  return (
    <Filter
      ref={filterRef}
      value={formValue}
      inputList={inputListWithTips}
      onConfirm={onConfirm}
      onChange={onFilterChange}
      onReset={onReset}
      resetTip={resetTip}
      onToggleMoreFilter={onToggleMoreFilter}
      defaultValue={resetFilterValue} // 用于reset
      iconText={filterCfg?.iconText}
      buttonText={filterCfg?.buttonText}
      showButton={filterCfg?.showConfirmButton}
      isHaveFilterSave={filterCfg?.haveFilterSaveBtn}
      isCustomLayoutFilter={filterCfg?.isCustomLayoutFilter}
      filterSaveType={filterCfg?.filterSaveType}
      filterGroups={filterCfg?.filterGroups}
      enableSticky={filterCfg?.enableSticky}
      stickyTopOffset={filterCfg?.stickyTopOffset}
      stickyHeaderTitle={filterCfg?.stickyHeaderTitle}
      useAutoCollapse={filterCfg?.useAutoCollapse}
    />
  );
}

export default React.forwardRef(DbiCommonFilter);
